import { CandidateRef } from "src/_common/utils/candidateRef";
import { ACTIONS } from "src/_config";

export interface ChatConversation {
  conversation_id: string;
  employer_company_id: number;
  employer_user_id: number;
  jobseeker_id: CandidateRef;
  jobseeker_name: string | null;
  is_blocked: boolean;
  has_messages: boolean;
  blocked_at: string | null;
  last_message_text: string | null;
  last_message_at: string | null;
  last_message_sender: "employer" | "jobseeker" | null;
  employer_unread_count: number;
  jobseeker_unread_count: number;
  created_at: string;
  updated_at: string;
}

export interface ChatMessageItem {
  message_id: string;
  conversation_id: string;
  sender_type: "employer" | "jobseeker";
  sender_id: number;
  message_text: string | null;
  attachment_url: string | null;
  attachment_name: string | null;
  attachment_type: string | null;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ChatState {
  conversations: ChatConversation[];
  activeConversationId: string | null;
  messages: Record<string, ChatMessageItem[]>;
  unreadCount: number;
}

const initialState: ChatState = {
  conversations: [],
  activeConversationId: null,
  messages: {},
  unreadCount: 0,
};

const chatReducer = (state: ChatState = initialState, action: any): ChatState => {
  switch (action.type) {
    case ACTIONS.CHAT.SET_CONVERSATIONS:
      return { ...state, conversations: action.payload };

    case ACTIONS.CHAT.SET_ACTIVE_CONVERSATION:
      return { ...state, activeConversationId: action.payload };

    case ACTIONS.CHAT.SET_MESSAGES:
      return {
        ...state,
        messages: {
          ...state.messages,
          [action.payload.conversationId]: action.payload.messages,
        },
      };

    case ACTIONS.CHAT.APPEND_MESSAGE: {
      const { conversationId, message } = action.payload;
      const existing = state.messages[conversationId] || [];
      // Avoid duplicates (e.g. when sender receives their own Pusher event)
      const isDuplicate = existing.some((m) => m.message_id === message.message_id);
      if (isDuplicate) return state;
      return {
        ...state,
        messages: {
          ...state.messages,
          [conversationId]: [...existing, message],
        },
      };
    }

    case ACTIONS.CHAT.SET_UNREAD_COUNT:
      console.log('[chatReducer] SET_UNREAD_COUNT:', action.payload);
      return { ...state, unreadCount: action.payload };

    case ACTIONS.CHAT.UPDATE_CONVERSATION_PREVIEW: {
      const { conversationId, lastMessageText, lastMessageAt, lastMessageSender } = action.payload;
      const updatedConversations = state.conversations.map((c) =>
        c.conversation_id === conversationId
          ? {
              ...c,
              last_message_text: lastMessageText,
              last_message_at: lastMessageAt,
              last_message_sender: lastMessageSender,
              employer_unread_count:
                lastMessageSender === "jobseeker"
                  ? c.employer_unread_count + 1
                  : c.employer_unread_count,
            }
          : c
      );
      // Recalculate total unread count from all conversations
      const newTotalUnread = updatedConversations.reduce((sum, c) => sum + (c.employer_unread_count || 0), 0);
      return {
        ...state,
        conversations: updatedConversations,
        unreadCount: newTotalUnread,
      };
    }

    case ACTIONS.CHAT.SET_BLOCKED: {
      const { conversationId, isBlocked } = action.payload;
      return {
        ...state,
        conversations: state.conversations.map((c) =>
          c.conversation_id === conversationId ? { ...c, is_blocked: isBlocked } : c
        ),
      };
    }

    case ACTIONS.CHAT.ADD_CONVERSATION: {
      const conversation = action.payload;
      // Check if conversation already exists in list
      const exists = state.conversations.some((c) => c.conversation_id === conversation.conversation_id);
      if (exists) {
        // Update existing conversation
        return {
          ...state,
          conversations: state.conversations.map((c) =>
            c.conversation_id === conversation.conversation_id ? { ...c, ...conversation } : c
          ),
        };
      }
      // Add to beginning of list (most recent)
      return {
        ...state,
        conversations: [conversation, ...state.conversations],
      };
    }

    case ACTIONS.CHAT.RESET_CONVERSATION_UNREAD: {
      const conversationId = action.payload;
      const updatedConversations = state.conversations.map((c) =>
        c.conversation_id === conversationId ? { ...c, employer_unread_count: 0 } : c
      );
      // Recalculate total unread count from all conversations
      const newTotalUnread = updatedConversations.reduce((sum, c) => sum + (c.employer_unread_count || 0), 0);
      return {
        ...state,
        conversations: updatedConversations,
        unreadCount: newTotalUnread,
      };
    }

    case ACTIONS.CHAT.UPDATE_CONVERSATION_FROM_PUSHER: {
      const { conversation_id, last_message_text, last_message_at, last_message_sender, employer_unread_count } = action.payload;
      console.log('[chatReducer] UPDATE_CONVERSATION_FROM_PUSHER for:', conversation_id);
      console.log('[chatReducer] Current conversations count:', state.conversations.length);
      
      // Check if conversation exists in the list
      const conversationExists = state.conversations.some((c) => c.conversation_id === conversation_id);
      console.log('[chatReducer] Conversation exists in list:', conversationExists);
      
      let updatedConversations;
      if (conversationExists) {
        // Update existing conversation
        updatedConversations = state.conversations.map((c) =>
          c.conversation_id === conversation_id
            ? {
                ...c,
                last_message_text,
                last_message_at,
                last_message_sender,
                employer_unread_count,
              }
            : c
        );
        console.log('[chatReducer] Updated conversation in list');
      } else {
        // If conversation doesn't exist, it might be a new conversation - just keep current list
        // The conversation will be loaded when user opens the messages page
        updatedConversations = state.conversations;
        console.log('[chatReducer] Conversation not in list, skipping update');
      }
      
      // Don't recalculate - use the total_unread that was set by SET_UNREAD_COUNT
      return {
        ...state,
        conversations: updatedConversations,
      };
    }

    default:
      return state;
  }
};

export default chatReducer;
